import { getDataAPI, postDataAPI } from './fetchData';
import { getActiveReminders } from './remindersData';

/**
 * Daily Digest Utility
 * Fetches and formats daily digest data from various sources
 * Uses backend persistence for reliable tracking across devices
 */

// Get the token for API calls
const getToken = () => {
    try {
        return JSON.parse(localStorage.getItem('dizeeToken'));
    } catch {
        return null;
    }
};

// Fetch daily digest status from backend
export const fetchDailyDigestStatus = async () => {
    try {
        const token = getToken();
        if (!token) {
            console.log('📊 No auth token available for daily digest check');
            return null;
        }

        const response = await getDataAPI('user/daily-digest/status', token);
        
        if (response.data.status === 200) {
            return response.data.data.lastShown;
        }
        return null;
    } catch (error) {
        console.error('📊 Error fetching daily digest status from backend:', error);
        // Fallback to localStorage if backend fails
        const lastShown = localStorage.getItem('dailyDigestLastShown');
        return lastShown ? parseInt(lastShown) : null;
    }
};

// Check if digest should be shown (after 5 AM daily reset in user's local timezone)
export const shouldShowDailyDigest = async () => {
    // Don't show digest if user hasn't completed onboarding (walkthrough + tutorial)
    const hasSeenWalkthrough = localStorage.getItem('hasSeenWalkthrough');
    const hasSeenTutorial = localStorage.getItem('hasSeenTutorial');
    
    // If user hasn't completed onboarding, don't show digest
    if (!hasSeenWalkthrough || !hasSeenTutorial) {
        console.log('📊 Daily Digest Check: User has not completed onboarding yet');
        return false;
    }
    
    // Check if user just completed onboarding (don't show digest on the same day)
    const onboardingCompletedDate = localStorage.getItem('onboardingCompletedDate');
    const now = new Date();
    
    // If we don't have an onboarding completion date, set it now (for existing users)
    if (!onboardingCompletedDate) {
        localStorage.setItem('onboardingCompletedDate', now.toISOString());
        console.log('📊 Daily Digest Check: Onboarding just completed, not showing digest today');
        return false;
    }
    
    // Don't show digest on the same day as onboarding completion
    const completedDate = new Date(onboardingCompletedDate);
    const completedDateOnly = new Date(completedDate.getFullYear(), completedDate.getMonth(), completedDate.getDate());
    const todayDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    if (completedDateOnly.getTime() === todayDateOnly.getTime()) {
        console.log('📊 Daily Digest Check: Onboarding completed today, not showing digest yet');
        return false;
    }
    
    // Try to get last shown from backend first, fallback to localStorage
    let lastShown = await fetchDailyDigestStatus();
    
    // If backend call failed, check localStorage as fallback
    if (lastShown === null) {
        const localLastShown = localStorage.getItem('dailyDigestLastShown');
        lastShown = localLastShown ? parseInt(localLastShown) : null;
    }
    
    // Get today at 5 AM in user's local timezone
    const today5AM = new Date();
    today5AM.setHours(5, 0, 0, 0);
    
    // If current time is before 5 AM, use yesterday's 5 AM as the cutoff
    if (now.getHours() < 5) {
        today5AM.setDate(today5AM.getDate() - 1);
    }
    
    console.log('📊 Daily Digest Check:', {
        currentTime: now.toLocaleString(),
        today5AM: today5AM.toLocaleString(),
        lastShown: lastShown ? new Date(lastShown).toLocaleString() : 'never',
        onboardingCompleted: completedDate.toLocaleString(),
        hasCompletedOnboarding: true,
        shouldShow: !lastShown || new Date(lastShown) < today5AM
    });
    
    // Show digest if:
    // 1. Never shown before, OR
    // 2. Last shown was before today's 5 AM
    if (!lastShown) return true;
    
    const lastShownDate = new Date(lastShown);
    return lastShownDate < today5AM;
};

// Synchronous version for immediate checks (uses localStorage cache)
export const shouldShowDailyDigestSync = () => {
    // Don't show digest if user hasn't completed onboarding (walkthrough + tutorial)
    const hasSeenWalkthrough = localStorage.getItem('hasSeenWalkthrough');
    const hasSeenTutorial = localStorage.getItem('hasSeenTutorial');
    
    if (!hasSeenWalkthrough || !hasSeenTutorial) {
        return false;
    }
    
    // Check if user just completed onboarding (don't show digest on the same day)
    const onboardingCompletedDate = localStorage.getItem('onboardingCompletedDate');
    const now = new Date();
    
    if (!onboardingCompletedDate) {
        localStorage.setItem('onboardingCompletedDate', now.toISOString());
        return false;
    }
    
    const completedDate = new Date(onboardingCompletedDate);
    const completedDateOnly = new Date(completedDate.getFullYear(), completedDate.getMonth(), completedDate.getDate());
    const todayDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    if (completedDateOnly.getTime() === todayDateOnly.getTime()) {
        return false;
    }
    
    // Use localStorage for immediate check
    const lastShown = localStorage.getItem('dailyDigestLastShown');
    
    const today5AM = new Date();
    today5AM.setHours(5, 0, 0, 0);
    
    if (now.getHours() < 5) {
        today5AM.setDate(today5AM.getDate() - 1);
    }
    
    if (!lastShown) return true;
    
    const lastShownDate = new Date(parseInt(lastShown));
    return lastShownDate < today5AM;
};

// Mark digest as shown (updates both backend and localStorage)
export const markDigestAsShown = async () => {
    const timestamp = Date.now();
    
    // Update localStorage immediately for instant UI feedback
    localStorage.setItem('dailyDigestLastShown', timestamp.toString());
    
    // Update backend for persistence
    try {
        const token = getToken();
        if (!token) {
            console.log('📊 No auth token available, digest status saved locally only');
            return;
        }

        await postDataAPI('user/daily-digest/mark-shown', {}, token);
        console.log('📊 Daily digest marked as shown in backend at:', new Date(timestamp).toLocaleString());
    } catch (error) {
        console.error('📊 Error marking digest as shown in backend:', error);
        // LocalStorage update already happened, so UI will still work
    }
};

// Get tasks due today
const getTasksDueToday = () => {
    try {
        const reminders = getActiveReminders();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        return reminders.filter(reminder => {
            const dueDate = new Date(reminder.dueDate || reminder.due_date);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate.getTime() === today.getTime();
        });
    } catch (error) {
        console.error('Error fetching tasks due today:', error);
        return [];
    }
};

// Get tasks due tomorrow
const getTasksDueTomorrow = () => {
    try {
        const reminders = getActiveReminders();
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        
        return reminders.filter(reminder => {
            const dueDate = new Date(reminder.dueDate || reminder.due_date);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate.getTime() === tomorrow.getTime();
        });
    } catch (error) {
        console.error('Error fetching tasks due tomorrow:', error);
        return [];
    }
};

// Get confirmed shows this week
const getShowsThisWeek = async () => {
    try {
        const token = JSON.parse(localStorage.getItem('dizeeToken'));
        const response = await getDataAPI('user/getBookings', token);
        
        if (response.data.status === 200 && response.data.data) {
            const bookingsData = Array.isArray(response.data.data) ? response.data.data : response.data.data.data;
            
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            const nextWeek = new Date(today);
            nextWeek.setDate(nextWeek.getDate() + 7);
            
            return bookingsData.filter(booking => {
                const perfDate = new Date(booking.perf_date);
                perfDate.setHours(0, 0, 0, 0);
                
                return booking.status === 'confirmed' && 
                       perfDate >= today && 
                       perfDate <= nextWeek;
            }).sort((a, b) => new Date(a.perf_date) - new Date(b.perf_date));
        }
        return [];
    } catch (error) {
        console.error('Error fetching shows this week:', error);
        return [];
    }
};

// Get releases this month
const getReleasesThisMonth = async () => {
    try {
        const token = JSON.parse(localStorage.getItem('dizeeToken'));
        const response = await getDataAPI('user/getCatalog', token);
        
        if (response.data.status === 200 && response.data.data) {
            const catalogData = Array.isArray(response.data.data) ? response.data.data : response.data.data.data;
            
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            endOfMonth.setHours(23, 59, 59, 999);
            
            return catalogData.filter(song => {
                if (!song.release_date) return false;
                
                const releaseDate = new Date(song.release_date);
                releaseDate.setHours(0, 0, 0, 0);
                
                // Upcoming releases only (status unreleased or future date)
                return (song.status === 'unreleased' || releaseDate >= today) && 
                       releaseDate >= today && 
                       releaseDate <= endOfMonth;
            }).sort((a, b) => new Date(a.release_date) - new Date(b.release_date));
        }
        return [];
    } catch (error) {
        console.error('Error fetching releases this month:', error);
        return [];
    }
};

// Get pending offers
const getPendingOffers = async () => {
    try {
        const token = JSON.parse(localStorage.getItem('dizeeToken'));
        const response = await getDataAPI('user/getBookings', token);
        
        if (response.data.status === 200 && response.data.data) {
            const bookingsData = Array.isArray(response.data.data) ? response.data.data : response.data.data.data;
            
            return bookingsData.filter(booking => booking.status === 'pending')
                .sort((a, b) => {
                    // Sort by expiration date if available
                    if (a.offer_expiration && b.offer_expiration) {
                        return new Date(a.offer_expiration) - new Date(b.offer_expiration);
                    }
                    // Otherwise sort by performance date
                    return new Date(a.perf_date) - new Date(b.perf_date);
                });
        }
        return [];
    } catch (error) {
        console.error('Error fetching pending offers:', error);
        return [];
    }
};

// Generate mock data for testing
const generateMockDigestData = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 5);
    
    const nextMonth = new Date(today);
    nextMonth.setDate(nextMonth.getDate() + 15);
    
    return {
        tasksDueToday: [
            {
                title: 'Finalize album artwork',
                description: 'Send final versions to label'
            },
            {
                title: 'Review marketing plan',
                description: 'Check social media calendar'
            }
        ],
        tasksDueTomorrow: [
            {
                title: 'Studio session prep',
                description: 'Prepare tracks for recording'
            },
            {
                title: 'Call with manager',
                description: 'Discuss Q4 plans'
            },
            {
                title: 'Music video meeting',
                description: 'Review director concepts'
            }
        ],
        showsThisWeek: [
            {
                event_title: 'Summer Music Festival',
                venue: 'Red Rocks Amphitheatre',
                perf_date: nextWeek.toISOString(),
                city: 'Morrison',
                state: 'CO'
            },
            {
                event_title: 'Local Showcase',
                venue: 'The Blue Note',
                perf_date: tomorrow.toISOString(),
                city: 'New York',
                state: 'NY'
            }
        ],
        releasesThisMonth: [
            {
                song_name: 'Summer Nights',
                release_date: nextMonth.toISOString(),
                artist_name: 'Your Artist Name'
            },
            {
                song_name: 'City Lights (Remix)',
                release_date: nextMonth.toISOString(),
                artist_name: 'Your Artist Name'
            }
        ],
        pendingOffers: [
            {
                event_title: 'Festival Headliner Spot',
                venue: 'Coachella Valley Music Festival',
                perf_date: new Date(today.getFullYear(), today.getMonth() + 2, 15).toISOString(),
                offer_expiration: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3).toISOString()
            }
        ],
        hasData: true
    };
};

// Fetch all digest data
export const fetchDailyDigestData = async () => {
    try {
        const [
            tasksDueToday,
            tasksDueTomorrow,
            showsThisWeek,
            releasesThisMonth,
            pendingOffers
        ] = await Promise.all([
            Promise.resolve(getTasksDueToday()),
            Promise.resolve(getTasksDueTomorrow()),
            getShowsThisWeek(),
            getReleasesThisMonth(),
            getPendingOffers()
        ]);
        
        return {
            tasksDueToday,
            tasksDueTomorrow,
            showsThisWeek,
            releasesThisMonth,
            pendingOffers,
            hasData: tasksDueToday.length > 0 || 
                     tasksDueTomorrow.length > 0 || 
                     showsThisWeek.length > 0 || 
                     releasesThisMonth.length > 0 || 
                     pendingOffers.length > 0
        };
    } catch (error) {
        console.error('Error fetching daily digest data:', error);
        return {
            tasksDueToday: [],
            tasksDueTomorrow: [],
            showsThisWeek: [],
            releasesThisMonth: [],
            pendingOffers: [],
            hasData: false
        };
    }
};

// Format date for display
export const formatDigestDate = (dateString) => {
    const date = new Date(dateString);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
};

// Format date with day of week
export const formatDigestDateWithDay = (dateString) => {
    const date = new Date(dateString);
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
};

// Generate digest message text
export const generateDigestMessage = (digestData) => {
    const { tasksDueToday, tasksDueTomorrow, showsThisWeek, releasesThisMonth, pendingOffers } = digestData;
    
    if (!digestData.hasData) {
        return "Good morning! 🌅\n\nYou're all caught up! No urgent tasks, shows, or releases coming up. Have a great day!";
    }
    
    let message = "Good morning! 🌅\n\nHere's your daily digest:\n\n";
    
    // Tasks Due Today
    if (tasksDueToday.length > 0) {
        message += "📋 **Tasks Due Today**\n";
        tasksDueToday.forEach((task, index) => {
            message += `${index + 1}. ${task.title || task.releaseName}\n`;
        });
        message += "\n";
    }
    
    // Tasks Due Tomorrow
    if (tasksDueTomorrow.length > 0) {
        message += "📅 **Coming Up Tomorrow**\n";
        tasksDueTomorrow.slice(0, 3).forEach((task, index) => {
            message += `${index + 1}. ${task.title || task.releaseName}\n`;
        });
        message += "\n";
    }
    
    // Shows This Week
    if (showsThisWeek.length > 0) {
        message += "🎤 **Shows This Week**\n";
        showsThisWeek.forEach((show, index) => {
            message += `${index + 1}. ${show.event_title || show.venue} - ${formatDigestDateWithDay(show.perf_date)}\n`;
        });
        message += "\n";
    }
    
    // Releases This Month
    if (releasesThisMonth.length > 0) {
        message += "🎵 **Releases This Month**\n";
        releasesThisMonth.forEach((release, index) => {
            message += `${index + 1}. ${release.song_name} - ${formatDigestDate(release.release_date)}\n`;
        });
        message += "\n";
    }
    
    // Pending Offers
    if (pendingOffers.length > 0) {
        message += "⚠️ **Pending Offers (Action Required)**\n";
        pendingOffers.forEach((offer, index) => {
            const expirationText = offer.offer_expiration ? 
                ` (Expires: ${formatDigestDate(offer.offer_expiration)})` : '';
            message += `${index + 1}. ${offer.event_title || offer.venue}${expirationText}\n`;
        });
        message += "\n";
    }
    
    message += "Have a productive day! 🚀";
    
    return message;
};

